import { lazy, Suspense, useEffect, useState, type CSSProperties } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RightPanel } from "./components/RightPanel";
import { Sidebar } from "./components/Sidebar";
import { Toolbar } from "./components/Toolbar";
import { useOrgStore } from "./state/orgStore";
import type { AppMode } from "./types/org";

type ModalName = "import" | "issues" | "versions" | "snapshots" | "export" | undefined;

const ExportModal = lazy(() => import("./components/ExportModal").then((module) => ({ default: module.ExportModal })));
const ImportModal = lazy(() => import("./components/ImportModal").then((module) => ({ default: module.ImportModal })));
const IssuesModal = lazy(() => import("./components/IssuesModal").then((module) => ({ default: module.IssuesModal })));
const PresentationOrgChart = lazy(() => import("./components/PresentationOrgChart").then((module) => ({ default: module.PresentationOrgChart })));
const SnapshotsModal = lazy(() => import("./components/SnapshotsModal").then((module) => ({ default: module.SnapshotsModal })));
const VersionsModal = lazy(() => import("./components/VersionsModal").then((module) => ({ default: module.VersionsModal })));

export default function App() {
  const [modal, setModal] = useState<ModalName>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(292);
  const mode: AppMode = window.location.pathname.startsWith("/admin") ? "draft" : "official";
  const companyFilter = useOrgStore((store) => store.state.companyFilter);
  const setMode = useOrgStore((store) => store.setMode);
  const loadPeriod = useOrgStore((store) => store.loadPeriod);
  const loadingInitialData = useOrgStore((store) => store.loadingInitialData);
  const themeClass = companyFilter === "BenQ" || companyFilter === "BQP" ? "theme-benq" : "";

  useEffect(() => {
    setMode(mode);
    void loadPeriod(undefined, undefined, mode);
  }, [loadPeriod, mode, setMode]);

  return (
    <div className={`app-shell ${themeClass} mode-${mode}`}>
      <Toolbar
        mode={mode}
        onImport={() => setModal("import")}
        onIssues={() => setModal("issues")}
        onVersions={() => setModal("versions")}
        onSnapshots={() => setModal("snapshots")}
        onExport={() => setModal("export")}
      />
      <main className={`workspace ${sidebarCollapsed ? "sidebar-collapsed" : ""}`} style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
        <Sidebar collapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} />
        {!sidebarCollapsed ? (
          <div
            className="sidebar-resizer"
            role="separator"
            aria-label="調整左側面板寬度"
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              const startX = event.clientX;
              const startWidth = sidebarWidth;
              const move = (moveEvent: PointerEvent) => {
                setSidebarWidth(Math.min(460, Math.max(220, startWidth + moveEvent.clientX - startX)));
              };
              const stop = () => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", stop);
              };
              window.addEventListener("pointermove", move);
              window.addEventListener("pointerup", stop);
            }}
          />
        ) : null}
        <ErrorBoundary>
          <Suspense fallback={<div className="canvas-fallback"><strong>載入組織圖中...</strong></div>}>
            <PresentationOrgChart readOnly={mode === "official"} />
          </Suspense>
        </ErrorBoundary>
        <RightPanel readOnly={mode === "official"} />
      </main>
      <ErrorBoundary key={modal ?? "closed"}>
        <Suspense fallback={<div className="modal-backdrop"><div className="modal-loading">載入中...</div></div>}>
          {modal === "import" && mode === "draft" ? <ImportModal onClose={() => setModal(undefined)} /> : null}
          {modal === "issues" && mode === "draft" ? <IssuesModal onClose={() => setModal(undefined)} /> : null}
          {modal === "versions" ? <VersionsModal onClose={() => setModal(undefined)} /> : null}
          {modal === "snapshots" && mode === "draft" ? <SnapshotsModal onClose={() => setModal(undefined)} /> : null}
          {modal === "export" ? <ExportModal onClose={() => setModal(undefined)} /> : null}
        </Suspense>
      </ErrorBoundary>
      {loadingInitialData ? <div className="app-loading">載入組織資料中...</div> : null}
    </div>
  );
}
